import * as React from 'react';
import { useMemo, useState } from 'react';
import { withRouter } from 'react-router';
import { connect } from 'react-redux';
import { Button } from 'antd';
import InvoicesTable from '../InvoicesTable';
import AccessPermissionChecker from '../common/AccessPermissionChecker';
import { EmptyState } from '../empty_state/EmptyState';
import OWLink from '../OWLink';
import { OWSubTabs } from '../ow_sub_tabs/OWSubTabs';
import { PERMISSION_NAMES } from '../../utils/AuthUtils';
import { withUserType } from '../withUserType';
import queryString from 'querystring';

// The two lists can't be one query: `projectId` matches invoices booked straight to the
// project and `buyerProjectId` matches invoices reached through the project's work orders,
// and the API ANDs its filters — sending both returns the intersection, which is always
// empty. So they're two tables behind a sub-tab, each with its own redux collection so
// filters, sorting and pagination don't leak across.
const PROJECT_INVOICES_TAB_KEY = 'project';
const WORK_ORDER_INVOICES_TAB_KEY = 'workOrders';

const INVOICE_SUB_TABS = [
	{ key: PROJECT_INVOICES_TAB_KEY, label: 'Project invoices' },
	{ key: WORK_ORDER_INVOICES_TAB_KEY, label: 'Work order invoices' },
];

const DEFAULT_PAGE_SIZE = 5;

// Pagination, sorting and filters as the URL has them on first render. Read once — the
// tables own these from then on, and re-reading would fight them.
const getInitialTableState = (search: string) => {
	const queryParams = queryString.parse(search && search[0] === '?' ? search.slice(1) : search);
	const { current, pageSize, sort_by, order, ...urlFilters } = queryParams as any;
	// Each tab supplies its own anchor through preAppliedFilters, and the two cannot be
	// combined — the API ANDs its filters, and a project invoice has no work order while a
	// work-order invoice has no project_id, so the intersection is always empty. A link or
	// bookmark carrying either key would therefore blank the tab it does not belong to, so
	// neither is ever taken from the URL.
	const { projectId, buyerProjectId, ...filters } = urlFilters;
	return {
		pagination: { current: current || 1, pageSize: pageSize || DEFAULT_PAGE_SIZE },
		sorting: { sort_by: sort_by || 'createdAt', order: order || 'desc' },
		filters: Object.keys(filters).length > 0 ? filters : {},
	};
};

interface ProjectInvoicesPageProps {
	match: any;
	location: any;
	// Injected by withUserType.
	userType?: string;
}

const ProjectInvoicesPage: React.FC<ProjectInvoicesPageProps> = ({ match, location, userType }) => {
	// Project invoices lead: they're the list this page can filter correctly today.
	const [activeTab, setActiveTab] = useState(PROJECT_INVOICES_TAB_KEY);

	const projectId = match.params.id;
	// Keyed on the search string rather than computed every render, so the tables keep
	// the same initial props across re-renders.
	const initialTableState = useMemo(() => getInitialTableState(location.search), [location.search]);

	const renderAddInvoiceButton = (size?: 'large') => (
		<AccessPermissionChecker name={PERMISSION_NAMES.CAN_UPLOAD_INVOICES_FOR_SUPPLIER}>
			<OWLink sameParent to={`/${userType}/projects/detail/${projectId}/invoices/new`}>
				<Button size={size} type="primary">
					Add invoice
				</Button>
			</OWLink>
		</AccessPermissionChecker>
	);

	// The shared invoice empty state points at creating a work order, which is the one
	// thing a project invoice is defined by not having.
	const renderProjectInvoicesEmptyState = () => (
		<EmptyState
			graphic={
				<img
					alt=""
					style={{ marginBottom: 8 }}
					src="https://s3.amazonaws.com/mock-data-assets/categories/images/cactus.svg"
				/>
			}
			headline="No invoices billed directly to this project yet."
			body={
				<div style={{ textAlign: 'center' }}>
					<div style={{ maxWidth: 440, marginBottom: 16 }}>
						Upload an invoice that isn't tied to a work order. Invoices from the project's work
						orders are on the Work order invoices tab.
					</div>
					{renderAddInvoiceButton()}
				</div>
			}
		/>
	);

	// Inline beside the table's search, like the Service Providers page's tabs.
	const invoiceTabs = (
		<OWSubTabs inline tabs={INVOICE_SUB_TABS} activeKey={activeTab} onChange={setActiveTab} />
	);

	return (
		<div style={{ marginTop: '-0.5em', padding: '0em 1em' }}>
			{/* Only the active table is mounted, so each fetches on mount. URL sync is off:
			    the two tables would otherwise write the same current/pageSize/sort_by keys
			    and each would pick up the other's page on a tab switch. Any filters already
			    on the URL are still honoured once, through initialFilters. */}
			{activeTab === PROJECT_INVOICES_TAB_KEY ? (
				<InvoicesTable
					key={PROJECT_INVOICES_TAB_KEY}
					handleQueryParams={false}
					targetCollectionName="projectDirectInvoicesTable"
					preAppliedFilters={{ projectId }}
					initialFilters={initialTableState.filters}
					initialPagination={initialTableState.pagination}
					initialSorting={initialTableState.sorting}
					headerActions={renderAddInvoiceButton('large')}
					headerLeading={invoiceTabs}
					renderEmptyState={renderProjectInvoicesEmptyState}
				/>
			) : (
				// This tab lists the project's work-order invoices, and "Add invoice" only ever
				// creates a project invoice — so it has no header action of its own.
				<InvoicesTable
					key={WORK_ORDER_INVOICES_TAB_KEY}
					handleQueryParams={false}
					targetCollectionName="projectWorkOrderInvoicesTable"
					preAppliedFilters={{ buyerProjectId: projectId }}
					initialFilters={initialTableState.filters}
					initialPagination={initialTableState.pagination}
					initialSorting={initialTableState.sorting}
					headerLeading={invoiceTabs}
				/>
			)}
		</div>
	);
};

const mapStateToProps = (state, ownProps) => ({
	match: ownProps.match,
	location: ownProps.location,
});

export default withUserType(withRouter(connect(mapStateToProps)(ProjectInvoicesPage)));
